import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import useOrg from '@/contexts/OrgContext';
import { FLAGSHIP_ORG } from '@/lib/moduleAccess';
import { RUNMARK_ICON_HREF } from '@/components/brand/RunmarkMark';

// ── Whose product is this? ───────────────────────────────────────────────────
// The rule, from the owner: every other MCL business sees THEIR branding, never
// ours. If they have not uploaded a logo, show the company's name as text —
// never a placeholder, and never a silent fall back to Parcelink's mark. Their
// subcontractors see the same white label, not just the office.
//
// ── Two different questions, and they have different answers ────────────────
// AFTER sign-in the org comes from the SESSION, which is the governing rule of
// the whole tenancy model. BEFORE sign-in there is no session, so a login page
// can only go on the hostname — org_public_branding() matches the first label
// against orgs.subdomain, so northappraise.runmark.co.uk resolves and the
// shared host app.runmark.co.uk deliberately does not.
//
// That shared host is the interesting case. Nobody can know whose portal it is
// until they log in, so it must not guess — and it especially must not fall
// back to Parcelink, which is how a customer ends up meeting a rival's brand at
// the one moment brand matters. It shows neutral Runmark instead.

const PARCELINK = {
  org_key: FLAGSHIP_ORG,
  name: 'Parcelink',
  logo: '/logo.png?v=3',
  colour: null,
  wordmark: 'Parcelink',
};

// Neutral platform identity for a host we cannot attribute to any one tenant.
const RUNMARK = {
  org_key: null,
  name: 'Runmark',
  logo: null,
  colour: null,
  wordmark: 'Runmark',
};

export function isRunmarkHost(host = typeof window !== 'undefined' ? window.location.hostname : '') {
  return host === 'runmark.co.uk' || host.endsWith('.runmark.co.uk') || host.endsWith('runmark.localhost');
}

// Parcelink's own hosts, and the plain local dev hosts that stand in for them.
// Everything else (a Runmark host, a tenant's own domain) is the Runmark side
// of the product, where Parcelink's mark must never appear.
export function isParcelinkHost(host = typeof window !== 'undefined' ? window.location.hostname : '') {
  const h = String(host || '').toLowerCase();
  if (h === 'parcelink.org' || h.endsWith('.parcelink.org')) return true;
  if (h === 'localhost' || h === '127.0.0.1') return true;
  return h.endsWith('.localhost') && !h.includes('runmark');
}

function fromBranding(orgKey, name, branding, dark) {
  const b = branding ?? {};
  const logo = dark ? (b.logo_dark || b.logo_light) : (b.logo_light || b.logo_dark);
  const display = b.display_name || name || null;

  // The flagship keeps its own file-based logo so nothing about Parcelink's own
  // app changes. Everyone else gets what they uploaded, or their name.
  if (orgKey === FLAGSHIP_ORG && !logo) return { ...PARCELINK, colour: b.colour ?? null };

  return {
    org_key: orgKey ?? null,
    name: display,
    logo: logo || null,
    colour: b.colour ?? null,
    wordmark: display,
  };
}

// Resolves the identity to display. `dark` picks the dark-background logo where
// the tenant supplied one.
export function useOrgBranding({ dark = false } = {}) {
  const { org, orgKey, orgName, branding, loaded } = useOrg();
  const [byHost, setByHost] = useState(undefined); // undefined = not asked yet

  const signedIn = !!org?.org_key;

  useEffect(() => {
    if (signedIn) return undefined;
    let cancelled = false;
    supabase
      .rpc('org_public_branding', { p_host: window.location.hostname })
      .then(({ data }) => {
        if (cancelled) return;
        const row = Array.isArray(data) ? data[0] : data;
        setByHost(row ?? null);
      })
      .catch(() => { if (!cancelled) setByHost(null); });
    return () => { cancelled = true; };
  }, [signedIn]);

  if (signedIn) return fromBranding(orgKey, orgName, branding, dark);

  // Pre-login. A resolvable vanity host or custom domain gives us the tenant;
  // otherwise the platform's own neutral identity everywhere that is not
  // Parcelink's own host, and Parcelink there.
  if (byHost?.org_key) return fromBranding(byHost.org_key, byHost.name, byHost.branding, dark);
  if (!isParcelinkHost()) return RUNMARK;
  return PARCELINK;
}

// The mark itself: their logo, or their name in text. Never a placeholder and
// never somebody else's logo.
export default function OrgMark({
  dark = false,
  className = '',
  logoClassName = 'h-10 w-auto',
  textClassName = 'text-lg font-extrabold tracking-tight',
  showName = false,
  alt,
}) {
  const b = useOrgBranding({ dark });

  if (b.logo) {
    return (
      <span className={`flex items-center gap-2.5 ${className}`}>
        <img src={b.logo} alt={alt ?? b.name ?? ''} className={logoClassName} />
        {showName && b.wordmark && <span className={textClassName}>{b.wordmark}</span>}
      </span>
    );
  }

  // No logo uploaded — the company's name, as text. This is the specified
  // behaviour, not a degraded one.
  return <span className={`${textClassName} ${className}`}>{b.wordmark ?? b.name ?? ''}</span>;
}

// The browser tab is branding too, and it is the one that survives after
// someone switches away. The static shell is prerendered with Parcelink's title
// and favicon for every host, so a tenant's crew had "…| Parcelink" sitting in
// their tab the whole time they worked. The flagship is left alone; everyone
// else gets their own name, and their own logo as the icon where they uploaded
// one.
export function useOrgDocumentIdentity(pageTitle) {
  const b = useOrgBranding();
  const { org_key: orgKey, name, logo } = b;

  useEffect(() => {
    if (!name || orgKey === FLAGSHIP_ORG) return undefined;

    const previousTitle = document.title;
    document.title = pageTitle ? `${pageTitle} — ${name}` : name;

    // Their logo as the icon where they uploaded one; off Parcelink's hosts
    // with none, Runmark's own mark — the baked-in icon is Parcelink's, and
    // that is for Parcelink only.
    const icon = logo || (!isParcelinkHost() ? RUNMARK_ICON_HREF : null);
    let restoreIcons = () => {};
    if (icon) {
      const links = [...document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]')];
      const previous = links.map((l) => ({ el: l, href: l.href }));
      links.forEach((l) => { l.href = icon; });
      restoreIcons = () => previous.forEach(({ el, href }) => { el.href = href; });
    }

    return () => {
      document.title = previousTitle;
      restoreIcons();
    };
  }, [orgKey, name, logo, pageTitle]);
}
